/* @flow */

import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { StoreInfoContext } from "entrypoint/shared";
import { useTranslate } from "@awardit/react-use-translate";
import cn from "classnames";

import styles from "./styles.scss";

type Brand = {
  img: string,
  title: string,
  url?: string,
};

type Variant = "popular" | "trusted";

const popularBrands: { [string]: Array<Brand> } = {
  sv: [
    { img: "cervera", title: "Cervera", url: "/cervera" },
    { img: "ahlens", title: "Åhlens", url: "/ahlens" },
    { img: "stadium", title: "Stadium", url: "/stadium" },
    { img: "jula", title: "Jula", url: "/presentkort-jula" },
    { img: "h&m", title: "H&M", url: "/h-m-zg" },
    { img: "mio", title: "Mio", url: "/mio" },
    { img: "plantagen", title: "Plantagen", url: "/plantagen" },
    { img: "clasohlson", title: "Clas Ohlson", url: "/clas-ohlson" },
  ],

  da: [
    { img: "zalando", title: "Zalando", url: "/zalando-dkk" },
    { img: "tivoli", title: "Tivoli", url: "/tivolikort-11-maneder" },
    { img: "sas_logo_3kb", title: "SAS", url: "/sas-dkk" },
    { img: "bio_biografbillet_dk", title: "Biografbillet", url: "/cinema" },
    { img: "fashioncheque", title: "Fashioncheque", url: "/fashioncheque" },
    { img: "rituals", title: "Rituals", url: "/rituals" },
    { img: "adidas", title: "Adidas", url: "/gavekort-adidas" },
    { img: "smarta_saker", title: "Smarta saker", url: "/smartasaker-dkk" },
  ],
  fi: [
    { img: "jula", title: "Jula", url: "/jula" },
    { img: "stadium", title: "Stadium", url: "/stadium-eur" },
    { img: "fashioncheque", title: "Fashioncheque", url: "/fashioncheque" },
    { img: "zalando", title: "Zalando", url: "/zalando" },
    { img: "sas_logo_3kb", title: "SAS", url: "/sas-fin" },
    { img: "vero_moda", title: "Vero Moda", url: "/vero-moda" },
    { img: "gift4you", title: "Gift4You Bio", url: "/gift4you-leffaelamys-22-50-eur" },
    { img: "rituals", title: "Rituals", url: "/rituals-fi" },
  ],
  nb: [
    { img: "zalando", title: "Zalando", url: "/zalando-nok" },
    { img: "fashioncheque", title: "Fashioncheque", url: "/fashioncheque" },
    { img: "cervera", title: "Cervera", url: "/cervera" },
    { img: "stadium", title: "Stadium", url: "/stadium-outlet" },
    { img: "jula", title: "Jula", url: "/jula" },
    { img: "sas_logo_3kb", title: "SAS", url: "/sas-nok" },
    { img: "clasohlson", title: "Clas Ohlson", url: "/clas-ohlson-nok" },
    { img: "filmweb", title: "Filmweb", url: "/norsk-biobiljett-filmweb-norge" },
  ],
};

const trustedBrands = [
  { img: "ving", title: "Ving" },
  { img: "mcd", title: "McDonald's" },
  { img: "sas_logo_3kb", title: "SAS" },
  { img: "db_schenker", title: "DB Schenker" },
  { img: "vattenfall", title: "Vattenfall" },
  { img: "falck", title: "Falck" },
  { img: "wurth", title: "Wurth" },
  { img: "tele2", title: "Tele2" },
  { img: "region_dalarna", title: "Region Dalarna" },
  { img: "region_vaster", title: "Region Västernorrland" },
  { img: "scandic", title: "Scandic" },
  { img: "husman_hagberg", title: "HusmanHagberg" },
];

const getPopularBrands = (locale: ?string): Array<Brand> => {
  const localeKey = locale ? locale.slice(0, 2) : "sv";

  return popularBrands[localeKey] || popularBrands.sv;
};

const BrandCard = ({ brand, variant }: { brand: Brand, variant: Variant }): React$Node => {
  const className = cn(styles.brandCard, styles[`${variant}Card`]);

  return brand.url ? (
    <Link to={brand.url} className={className}>
      <img
        className={cn(styles.brandImage, styles[`${variant}Image`])}
        src={`/assets/redeem/${brand.img}.svg`}
        alt={brand.title}
      />
    </Link>
  ) : (
    <div className={className}>
      <img
        className={cn(styles.brandImage, styles[`${variant}Image`])}
        src={`/assets/redeem/${brand.img}.svg`}
        alt={brand.title}
      />
    </div>
  );
};

const PopularBrands = ({
  children,
  className,
  variant = "popular",
}: {
  children?: React$Node,
  className?: string,
  variant?: Variant,
}): React$Node => {
  const { info: { locale } } = useContext(StoreInfoContext);
  const t = useTranslate();
  const brands = variant === "trusted" ? trustedBrands : getPopularBrands(locale);
  const headings = {
    popular: t("COMPANYVIEW.POPULAR_TITLE"),
    trusted: t("COMPANYVIEW.TRUSTED_TITLE"),
  };

  if (brands.length === 0) {
    return null;
  }

  return (
    <section className={cn(styles.popularBrands, className)}>
      <h2 className={styles.heading}>{headings[variant]}</h2>
      <div className={cn(styles.brands, styles[variant])}>
        {brands.map(x => (
          <BrandCard key={x.title} brand={x} variant={variant} />
        ))}
      </div>
      {children}
    </section>
  );
};

export default PopularBrands;
